import React, { useEffect, useContext } from 'react';
import ReactGA from 'react-ga4';
import { useDispatch, useSelector } from 'react-redux';
import os from '@incentivegames/ig-types/lib/constants/os';

import { UserContext } from '@incentivegames/ig-frontend-common/lib/contexts/userContext';
import { useCurrentWidth } from '@incentivegames/ig-frontend-common/lib/hooks/useCurrentWidth';
import { communicationUtilities } from '@incentivegames/ig-frontend-common/lib/utilities/communicationUtilities';
import { dreamteamUtilities } from './utilities/dreamteamUtilities';
import { IInitialState } from 'interfaces/gameInterfaces';
import { IRootState } from 'store/serverInterfaces';
import { IGame } from '@incentivegames/ig-types/lib/interfaces/apiContracts';
import { cookieUtilities } from 'utilities/cookieUtilities';

import packageJson from '../package.json';
import Routes from './components/Routes';
import Config from './theme/config';

import { getGames, setPageFullyLoaded } from 'store/actions/dreamteamActions';

import './App.scss';
import './shared.scss';

const isDev = true;
communicationUtilities.addHeightObserver();

const App = () => {
  const dispatch = useDispatch();
  const dreamteamReducer: IInitialState = useSelector((state: IRootState) => state.DreamteamReducer);

  useEffect(() => {
    dispatch(getGames());
  }, [dispatch]);

  useEffect(() => {
    window.addEventListener('load', () => dispatch(setPageFullyLoaded(true)));

    window.addEventListener(
      'contextmenu',
      (e) => {
        e.preventDefault();
      },
      false
    );

    return () => {
      window.removeEventListener('load', () => dispatch(setPageFullyLoaded(true)));

      window.removeEventListener(
        'contextmenu',
        (e) => {
          e.preventDefault();
        },
        false
      );
    };
    // eslint-disable-next-line
  }, []);

  if (cookieUtilities.checkCookiesEnabled()) {
    console.log('Cookies are enabled');
    try {
      ReactGA.initialize(process.env.REACT_APP_GA_TRACKING_CODE || '');
    } catch (error) {
      console.log('Error initialising GA', error);
    }
  } else {
    console.log('Cookies are disabled');
  }

  const userContext = useContext(UserContext);
  let width = useCurrentWidth();

  useEffect(() => {
    const sendHeight = setTimeout(() => {
      communicationUtilities.sendPostMessage.resize();
    }, 200);

    return () => clearTimeout(sendHeight);
  }, [width]);

  /**
   * This is not ideal. The way we add listeners right now doesn't allow us to edit
   * the listeners with new values (in this case when the entry changes, we want to udpate the listener).
   * As temporary fix, I added here the listener.
   */
  useEffect(() => {
    const addListener = (event: MessageEvent) => {
      const isPhone = communicationUtilities.getOs() === os.ANDROID || communicationUtilities.getOs() === os.IOS;
      if (isPhone || (document.referrer && document.referrer !== '')) {
        if (isPhone || event.origin === new URL(document.referrer).origin) {
          switch (event.data.action) {
            case 'share':
              if (dreamteamReducer?.selectedEntry && dreamteamReducer?.games && dreamteamReducer?.selectedGameId && dreamteamReducer.selectedEntry.gameId === dreamteamReducer.selectedGameId) {
                communicationUtilities.sendPostMessage.shareEntry(
                  dreamteamUtilities.getShareEntry(dreamteamReducer.games.find((g: IGame) => g.gameId === dreamteamReducer.selectedGameId)?.gameName, dreamteamReducer.selectedEntry)
                );
              } else {
                communicationUtilities.sendPostMessage.shareEntry({});
              }
              break;
            default:
              break;
          }
        }
      }
    };
    window.addEventListener('message', addListener, false);
    return () => {
      window.removeEventListener('message', addListener, false);
    };
    // eslint-disable-next-line
  }, [dreamteamReducer.selectedEntry, dreamteamReducer.selectedGameId]);

  useEffect(() => {
    userContext.user.addConfigHandlers(
      (token: string, promotion?: string) => {
        if (isDev) {
          console.log('Promotion:', promotion);
        }

        userContext.setPlayerData(token, promotion);
      },
      (language: string) => userContext.setLanguage(language),
      (currency: string) => userContext.setCurrency(currency),
      undefined,
      undefined,
      undefined
    );

    // This enables scrolling for dev only, when on an iFrame the parent handles this
    if (process.env.REACT_APP_SITE_ENV === 'dev') {
      document.body.style.overflowY = 'scroll';
    }

    if (communicationUtilities.getOs() === os.IOS && (window as any).webkit) {
      document.body.style.overflowY = 'auto';
    }

    // This is to help local testing, if the siteId is the mock one,
    // the token will override the one in the config
    if (process.env.REACT_APP_SITE_ID === '00000000') {
      const url = new URL(window.location.href);
      const token = url.searchParams.get('token');
      if (token) userContext.setToken(token || '');
    }

    // eslint-disable-next-line
  }, []);

  return (
    <div className="virtual-league-app" id="game-app">
      <Routes />
      {Config.settings.poweredByIncentiveLogo ? (
        <div className="app-footer">
          <div className="footer"></div>
        </div>
      ) : (
        ''
      )}
      <div id="version" style={{ display: process.env.REACT_APP_SITE_ENV === 'dev' ? 'inline' : 'none', fontSize: '10px', position: 'absolute', bottom: '15px', right: '25px' }}>{`${
        packageJson.version
      } - H:${document.getElementById('root')?.scrollHeight}`}</div>
    </div>
  );
};

export default App;
